import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppsService } from '../../../services/apps/apps.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { AdminService } from '../../../services/admin/admin.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { AddMediasForAppComponent } from '../../../components/modals/add-medias-for-app/add-medias-for-app.component';
import { MatDialog } from '@angular/material/dialog';
import { IPreparedFile } from '../../../services/file-upload.service';
import { App } from '../../../interfaces/app';
import { pluck } from 'rxjs/operators';

type RestaurantSelectionMode = 'all' | 'custom';

type AppRestaurantsForm = {
  mode: FormControl<RestaurantSelectionMode>;
  toAll: FormControl<boolean>;
  ids: FormControl<number[] | null>;
};

type AppConfigurationForm = {
  name: FormControl<string>;
  description: FormControl<string>;
  restaurants: FormGroup<AppRestaurantsForm>;
  files: FormControl<number[]>;
  published: FormControl<boolean>;
  config: FormControl<unknown>;
};

@UntilDestroy()
@Component({
  selector: 'app-add-app-page',
  templateUrl: './add-app-page.component.html',
  styleUrls: ['./add-app-page.component.scss'],
  animations: [
    trigger('slideFormField', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class AppAddAppPageComponent implements OnInit {
  public chainId!: number;
  public appId!: string;
  public isLoadingSubject = new BehaviorSubject(false);
  public allRestaurants$!: Observable<IRestaurantInfo[]>;
  public files: IPreparedFile[]  = [];
  public app!: App;

  form = new FormGroup<AppConfigurationForm>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
    description: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    restaurants: new FormGroup<AppRestaurantsForm>({
      mode: new FormControl('all', { nonNullable: true }),
      toAll: new FormControl(true, { nonNullable: true }),
      ids: new FormControl<number[] | null>(null),
    }),
    files: new FormControl<number[]>([], { nonNullable: true }),
    published: new FormControl(false, { nonNullable: true }),
    config: new FormControl<unknown>({}, { nonNullable: true }),
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apps: AppsService,
    private readonly adminService: AdminService,
    private readonly matDialog: MatDialog,
    private readonly activateRoute: ActivatedRoute,
  ) {
    this.activateRoute.data.pipe(
      pluck('app'),
      untilDestroyed(this),
    ).subscribe((app: App) => {
      this.app = app;
      this.form.get('config')?.setValue(this.app.configDefault || {});
    });
  }

  ngOnInit(): void {
    this.chainId = Number(this.route.parent?.snapshot.paramMap.get('id'));
    this.appId = this.route.snapshot.paramMap.get('appId') || '';
    this.getAllRestaurants();

    const { mode: modeControl, ids: idsControl } = this.restaurantsFormGroup.controls;

    modeControl.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe((mode): void => {
      if (mode === 'custom') {
        idsControl.setValidators([Validators.required]);
      } else {
        idsControl.clearValidators();
        idsControl.setValue(null);
      }
      idsControl.updateValueAndValidity();
    });
  }

  get restaurantsFormGroup(): FormGroup<AppRestaurantsForm> {
    return this.form.controls.restaurants;
  }

  public getAllRestaurants(): void {
    this.allRestaurants$ = this.adminService.getRestaurantsByChainId(this.chainId);
  }

  get isLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  public create(): void {
    this.isLoadingSubject.next(true);

    const formValue = this.form.getRawValue();
    const restaurantsValue = formValue.restaurants;

    this.apps.configureAppForChain(
      this.chainId,
      {
        appId: this.appId,
        name: formValue.name,
        description: formValue.description,
        published: formValue.published,
        restaurants: restaurantsValue.toAll ? { all: true, ids: [] } : { all: false, ids: restaurantsValue.ids || [] },
        files: formValue.files,
        config: formValue.config,
      }
    ).pipe(
      untilDestroyed(this)
    ).subscribe({
      next: () => {
        this.isLoadingSubject.next(false);
        this.backToAppsList();
      },
      error: () => {
        this.isLoadingSubject.next(false);
      }
    });
  }

  public addMedia(): void {
    const mediaModal = this.matDialog.open(AddMediasForAppComponent, {
      data: {
        files: this.files
      }
    });

    mediaModal.afterClosed().pipe(
      untilDestroyed(this)
    ).subscribe((result) => {
      if (result?.files) {
        this.files = result?.files;

        const filesControl = this.form.get('files');
        if (filesControl) {
          filesControl.setValue(this.files.map((file) => file.id));
        }
      }
    });
  }

  public backToAppsList(): void {
    this.router.navigate(['/chains', this.chainId.toString(), 'apps', 'app', this.appId]);
  }

}
